import { gsap } from "gsap";
import { Sprite, Texture } from "pixi.js";

import { AbstractSpine } from "../../../../abstracts/graphics/AbstractSpine";
import { playSound, stopSound } from "../../../../abstracts/utils/SoundUtils";
import { Apocalypse } from "../../../Apocalypse";
import {
    ApocalypseSoundNames,
    ApocalypseSpineNames,
} from "../../../assetsLoader/ApocalypseAssetsNames";
import { symbolConfig } from "../../../config/ApocalypseConfig";
import { ApocalypseSymbolNames } from "../../../types/ApocalypseEnums";
import { ApocalypseSymbol } from "./ApocalypseSymbol";

export class ApocalypseWildSymbol extends ApocalypseSymbol {
    private firstSkinName: string;
    private secondSkinName: string;

    private sxnSpine: AbstractSpine;
    private reelId: number = 2;

    constructor() {
        super(ApocalypseSymbolNames.WILD);

        this.isWild = true;
    }

    protected generateSymbol() {
        this.firstSkinName = `Wild1`;
        this.secondSkinName = `Wild2`;

        this.mainSpine = new AbstractSpine(ApocalypseSpineNames.WILD);
        this.mainSpine.setSkinByName(this.firstSkinName);

        this.mainSpine.getSpineTween(`Normal`);

        this.mainSpineDefaultXPosition = symbolConfig.symbolWidth / 2;
        this.mainSpineDefaultYPosition = symbolConfig.symbolHeight / 2;

        this.mainTextureDefaultName = `Apocalypse/Wild1`;

        this.mainTextureSprite = new Sprite(Texture.from(this.mainTextureDefaultName));
        this.mainTextureSprite.name = `mainTextureSprite`;
        this.mainTextureSprite.scale.set(0.5);
        this.mainTextureSprite.alpha = 1;

        this.mainTextureDefaultXPosition =
            symbolConfig.symbolWidth / 2 - this.mainTextureSprite.width / 2;
        this.mainTextureDefaultYPosition =
            symbolConfig.symbolHeight - this.mainTextureSprite.height;

        this.addChild(this.mainTextureSprite);

        this.mainSpine.x = this.mainSpineDefaultXPosition;
        this.mainSpine.y = this.mainSpineDefaultYPosition;

        this.mainTextureSprite.x = this.mainTextureDefaultXPosition;
        this.mainTextureSprite.y = this.mainTextureDefaultYPosition;

        this.reelId = 2;
    }

    public changeSymbolTexture(reelId: number) {
        this.reelId = reelId;

        this.removeChild(this.mainSpine);
        this.mainSpine.setSkinByName(reelId % 2 === 0 ? this.firstSkinName : this.secondSkinName);

        this.mainTextureDefaultName = `Apocalypse/${
            reelId % 2 === 0 ? this.firstSkinName : this.secondSkinName
        }`;

        this.mainTextureSprite.texture = Texture.from(this.mainTextureDefaultName);
        this.addChild(this.mainTextureSprite);
    }

    override getLandingAnimation(): gsap.core.Tween | gsap.core.Timeline {
        return this.mainSpine.getSpineTween(`Landing`, false, () => {
            if (this.reelId === 2) {
                playSound({ name: ApocalypseSoundNames.WILD_LAND_1 });
            } else {
                stopSound({ name: ApocalypseSoundNames.WILD_LAND_1 });
            }
            this.addChild(this.mainSpine);
            this.removeChild(this.mainTextureSprite);
        });
    }

    public override getWinAnimation(): gsap.core.Tween | gsap.core.Timeline {
        const tl = gsap.timeline();

        if (Apocalypse.model.hasWildsToNudge) {
            const animation = this.getIdleAnimation();
            animation.timeScale(1.6);

            tl.add(animation);
        } else {
            const symbolWinAnimation = this.mainSpine.getSpineTween(`Win`, false, () => {
                this.mainSpine.tint = 0xffffff;

                // @ts-ignore
                this.mainSpine.filters = null;

                this.addChild(this.mainSpine);
                this.removeChild(this.mainTextureSprite);
            });

            tl.add(symbolWinAnimation);

            const symbolWinIdleAnimation = this.mainSpine.getSpineTween(`Win_idle`);

            tl.add(symbolWinIdleAnimation);
            tl.timeScale(1.4);
        }

        return tl;
    }

    public getNoWinAnimation(): gsap.core.Tween | gsap.core.Timeline {
        const symbolAnimation = gsap.to(this.mainSpine, {
            pixi: { tint: 0x555555 },
            duration: 0.1,

            onStart: () => {
                this.mainSpine.getSpineTween(`Normal`);
            },
        });

        return symbolAnimation;
    }

    public getSuperXNudgeAnimation(): gsap.core.Timeline {
        this.isActivated = true;

        const sxnSoundName = `superXnudgeX${this.animationId}`;

        const tl = gsap.timeline({
            smoothChildTiming: true,
            autoRemoveChildren: true,
            onStart: () => {
                if (Apocalypse.model.data.totalSpinWinnings > 0) {
                    playSound({ name: sxnSoundName });
                }
            },
        });

        const connectAnimation = this.mainSpine.getSpineTween(`connect`);
        tl.add(connectAnimation);

        if (this.mainSpine.skeleton.skin.name === this.secondSkinName) {
            this.sxnSpine = new AbstractSpine(ApocalypseSpineNames.SUPER_XNUDGE);

            this.size = 4;

            const getYPosition = () => {
                switch (this.positionInArray) {
                    case 1:
                        return 55;
                    case 2:
                        return -52;
                    case 3:
                        return -159;

                    default:
                        return -266;
                }
            };
            this.sxnSpine.y = getYPosition();
            // playSound({ name: `scatter4Land` });

            tl.add(
                this.sxnSpine.getSpineTween(
                    `new_${this.animationId}x_move`,
                    false,
                    () => {
                        this.removeChild(this.mainTextureSprite);
                        this.addChild(this.sxnSpine);
                        this.parent.addChild(this);
                    },
                    null,
                    () => {
                        // stopSound({ name: sxnSoundName });
                        // ! poate aici ar trebui sa faci stop?
                    }
                )
            );
        }

        return tl;
    }

    private get animationId(): number {
        let animationId: number = 1;
        switch (this.positionInArray) {
            case 1:
                animationId = 8;
                break;

            case 2:
                animationId = 6;

                break;

            case 3:
                animationId = 4;

                break;
        }

        return animationId;
    }

    public getIdleAnimation(): gsap.core.Tween {
        return this.sxnSpine.getSpineTween(`new_${this.animationId}x_move_idle`, true, () => {
            let yPosition = -266;

            switch (this.positionInArray) {
                case 1:
                    yPosition = 62;
                    break;

                case 2:
                    yPosition = -51;

                    break;

                case 3:
                    yPosition = -158;

                    break;
            }

            this.sxnSpine.y = yPosition;
        });
    }

    public getStaticAnimation() {
        const animationName = `new_${this.animationId}x_move_static`;

        this.sxnSpine.reset();
        this.addChild(this.sxnSpine);
        this.sxnSpine.state.setAnimation(0, animationName, false);

        if (this.positionInArray === 4) {
            this.sxnSpine.y = -266;
        }
    }

    override reset() {
        super.reset();

        this.isWild = true;

        if (this.sxnSpine !== undefined) {
            this.sxnSpine.reset();
            this.removeChild(this.sxnSpine);
        }

        this.isActivated = false;

        this.size = 1;
    }

    resetOnStartSpin() {
        super.resetOnStartSpin();

        this.isWild = true;

        if (this.isActivated) {
            this.removeChild(this.mainTextureSprite);
        }
    }
}
